/// <amd-module name="TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/PanelAboutRegion/ContributedAboutRegion" />
import * as React from "react";

import { FormItem } from "VSSUI/FormItem";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";

import { IProjectAboutRegionProvider } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Components/AboutProject/PanelAboutRegion/Interfaces";
import { TfvcPrefix } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Constants";
import { AboutRegionInfo, SourceRepositoryTypes } from "TFS/Views/ProjectOverviewPage/ProjectOverviewTab/Models";
import * as Resources from "TFS/Views/ProjectOverviewPage/Resources";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { RadioButtonGroupDirection } from "VSSUI/Components/RadioButton/RadioButton.Props";

export interface IContributedAboutRegionProps extends IVssComponentProperties {
    projectName: string;
    readMode: boolean;
    showError: boolean;
    disableEditing?: boolean;
    selectedRepoType: SourceRepositoryTypes | undefined;
    selectedRepoId: string | undefined;
    onAboutSourceChanged?: (repoType?: SourceRepositoryTypes, repoId?: string) => void;
    onMarkTTI?: (source: string) => void;
}

export interface IContributedAboutRegionState {
    aboutContentProviders: { [key: string]: IProjectAboutRegionProvider };
    loadingProviders: boolean;
}

namespace ProjectAboutRegionProviders {
    // we need this info to map existing project settings with providerIds.
    export const wikiProviderId = "ms.vss-wiki-web.wiki-project-about-provider";
    export const codeProviderId = "ms.vss-code-web.vc-project-about-provider";
}

export class ContributedAboutRegion extends VssComponent<IContributedAboutRegionProps, IContributedAboutRegionState> {
    private readonly _contributionService: IVssContributionService;
    private _aboutSettingsMap: { [key: string]: AboutRegionInfo };
    private readonly _aboutRegionProviderIds: string[] | undefined;
    private _performanceService: IVssPerformanceService;

    constructor(props: IContributedAboutRegionProps, context: IVssComponentContext) {
        super(props, context);

        this._contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this._aboutSettingsMap = {};

        this._aboutRegionProviderIds = this._contributionService.getContributionChildren(
            "ms.vss-tfs-web.project-overview-about-region",
            "ms.vss-web.service"
        );

        const initialSelectedProviderId = _getAboutRegionProviderId(this._aboutRegionProviderIds, props.selectedRepoType);
        if (initialSelectedProviderId) {
            const initialRepoId = _getRepoId(props.selectedRepoId, props.selectedRepoType, props.projectName);
            this._aboutSettingsMap[initialSelectedProviderId] = { repositoryId: initialRepoId, repositoryType: props.selectedRepoType };
        }
        this._performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");

        this.state = {
            aboutContentProviders: {},
            loadingProviders: true
        };
    }

    public componentDidMount(): void {
        super.componentDidMount();
        this._performanceService.addSplitTiming("ContributedAboutRegion.FetchContentProviders.Started");

        this._fetchAboutContentProviders();
    }

    public componentDidUpdate(prevProps: IContributedAboutRegionProps, prevState: IContributedAboutRegionState): void {
        super.componentDidUpdate(prevProps, prevState);

        this._fetchAboutContentProviders();
    }

    public render(): JSX.Element | null {
        return (
            <div className="about-section">
                {this.props.readMode ? this._renderContent() : this._renderEditModeAboutRegion()}
            </div>
        );
    }

    private _renderContent(): JSX.Element | null {
        const selectedId = _getAboutRegionProviderId(this._aboutRegionProviderIds, this.props.selectedRepoType);
        if (selectedId) {
            if (this.state.aboutContentProviders[selectedId]) {
                return this.state.aboutContentProviders[selectedId].getAboutRegion(
                    this.props.readMode,
                    _getRepoId(this.props.selectedRepoId, this.props.selectedRepoType, this.props.projectName),
                    this._onRepoIdChanged,
                    this._onMarkTimeToInteractive,
                    this.props.showError
                );
            } else {
                return <Spinner />;
            }
        } else {
            this._onMarkTimeToInteractive("ServiceDisabled");
            return null;
        }
    }

    private _renderEditModeAboutRegion(): JSX.Element {
        return this.state.loadingProviders ? (
            <Spinner />
        ) : (
            <>
                <RadioButtonGroup
                    text={Resources.AboutProjectPanelAboutSectionHeading}
                    selectedButtonId={_getAboutRegionProviderId(this._aboutRegionProviderIds, this.props.selectedRepoType)}
                    className="about-choice-group"
                    onSelect={this._onRadioButtonSelect}
                    direction={RadioButtonGroupDirection.Horizontal}
                >
                    {this._getChangeReadmeRadioButtons()}
                </RadioButtonGroup>
                <div className="separator" />
                <div className="about-region-content">{this._renderContent()}</div>
            </>
        );
    }

    private _onMarkTimeToInteractive = (source: string): void => {
        if (this.props.onMarkTTI) {
            this.props.onMarkTTI(source);
        }
    };

    private _fetchAboutContentProviders(): void {
        if (!this._aboutRegionProviderIds) {
            return;
        }
        if (this.props.readMode) {
            const selectedId = _getAboutRegionProviderId(this._aboutRegionProviderIds, this.props.selectedRepoType);
            if (selectedId && !this.state.aboutContentProviders[selectedId]) {
                this._fetchAboutContentProvider(selectedId).then(() => {
                    this._performanceService.addSplitTiming("ContributedAboutRegion.FetchContentProviders.End");
                });
            }
        } else if (this.state.loadingProviders) {
            const promises = this._aboutRegionProviderIds.map(id => {
                return this._fetchAboutContentProvider(id);
            });
            Promise.all(promises).then(() => {
                this.setState({ loadingProviders: false });
            });
        }
    }

    private _fetchAboutContentProvider(providerId: string): Promise<void> {
        if (this.state.aboutContentProviders[providerId]) {
            return Promise.resolve();
        }

        return this.trackPromise(this._contributionService.getService<IProjectAboutRegionProvider>(providerId)).promise.then(
            provider => {
                if (provider) {
                    const aboutContentProviders = this.state.aboutContentProviders;
                    aboutContentProviders[providerId] = provider;
                    this.setState({ aboutContentProviders: aboutContentProviders });
                    return Promise.resolve();
                }
            },
            () => {
                // no-op
            }
        );
    }

    private _onRadioButtonSelect = (selectedButtonId: string): void => {
        if (selectedButtonId !== _getAboutRegionProviderId(this._aboutRegionProviderIds, this.props.selectedRepoType)) {
            if (!this._aboutSettingsMap[selectedButtonId]) {
                this._aboutSettingsMap[selectedButtonId] = {
                    repositoryId: undefined,
                    repositoryType: _getRepoType(undefined, selectedButtonId)
                };
            }
            const aboutInfo = this._aboutSettingsMap[selectedButtonId];
            this.props.onAboutSourceChanged && this.props.onAboutSourceChanged(aboutInfo.repositoryType, aboutInfo.repositoryId);
        }
    };

    private _getChangeReadmeRadioButtons(): JSX.Element[] {
        const radioButtons: JSX.Element[] = [];
        if (this._aboutRegionProviderIds) {
            for (const id of this._aboutRegionProviderIds) {
                const provider = this.state.aboutContentProviders[id];
                if (provider) {
                    radioButtons.push(
                        <RadioButton
                            className={provider.getChoiceName()}
                            id={id}
                            text={provider.getChoiceName()}
                            disabled={this.props.disableEditing}
                        />
                    );
                }
            }
        }
        return radioButtons;
    }

    private _onRepoIdChanged = (repoId: string): void => {
        const providerId = _getAboutRegionProviderId(this._aboutRegionProviderIds, this.props.selectedRepoType);
        const repoType = _getRepoType(repoId, providerId);
        this._aboutSettingsMap[providerId!] = { repositoryId: repoId, repositoryType: repoType };
        this.props.onAboutSourceChanged && this.props.onAboutSourceChanged(repoType, repoId);
    };
}

function _getRepoId(repoId: string | undefined, repoType: SourceRepositoryTypes | undefined, projectName: string): string | undefined {
    return repoType === SourceRepositoryTypes.Tfvc ? TfvcPrefix + projectName : repoId;
}

function _getAboutRegionProviderId(aboutRegionProviderIds: string[] | undefined, repoType: SourceRepositoryTypes | undefined): string | undefined {
    const providerId =
        repoType === SourceRepositoryTypes.Wiki ? ProjectAboutRegionProviders.wikiProviderId : ProjectAboutRegionProviders.codeProviderId;
    // we should return the id only if it's available via contributions.
    return aboutRegionProviderIds && aboutRegionProviderIds.find(id => id === providerId);
}

function _getRepoType(repoId: string | undefined, providerId?: string): SourceRepositoryTypes {
    if (repoId && repoId.startsWith(TfvcPrefix)) {
        return SourceRepositoryTypes.Tfvc;
    }

    return providerId === ProjectAboutRegionProviders.wikiProviderId ? SourceRepositoryTypes.Wiki : SourceRepositoryTypes.Git;
}
